import React, { Component } from "react";

import Body from "../Navigation/Body";
import PatientDashboard from "./PatientDashboard";
import { connect } from "react-redux";
import { patientActions } from "../../actions";

class PatientFetch extends React.Component {
  constructor(props) {
    super(props);
    this.state = { micro_id: null, ptObj: null };
    this.treeRequestId = 0;
    this.treeCache = new Map();
    this.treeDetailsCache = new Map();
  }

  componentDidMount() {
    console.log('Patient Fetch (mount) for', this.props.micro_id);
    this.resetConstraints();
    this.fetchPtData();
    this.fetchContactList();
    this.fetchTree(true);
    this.fetchTreeSnp2Snapshot();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.micro_id !== this.props.micro_id) {
      console.log('Patient Fetch (update) for', this.props.micro_id);
      this.treeCache.clear();
      this.treeDetailsCache.clear();
      this.resetConstraints();
      this.fetchPtData();
      this.fetchContactList();
      this.fetchTreeSnp2Snapshot();
      this.fetchTree(true);
    }

    // Tree depends on these
    if (
      prevProps.snp_distance !== this.props.snp_distance ||
      prevProps.constrain_tree !== this.props.constrain_tree
    ) {
      console.log('Patient Fetch (update) for tree', this.props.micro_id);
      this.fetchTree();
    }

    // Pt data also depends on overlap level/incubation windows etc.
    if (
      prevProps.overlap_level !== this.props.overlap_level ||
      prevProps.incubation !== this.props.incubation ||
      prevProps.inf_period_before !== this.props.inf_period_before ||
      prevProps.inf_period_after !== this.props.inf_period_after ||
      prevProps.contam_period !== this.props.contam_period ||
      prevProps.test_time_buffer !== this.props.test_time_buffer
    ) {
      console.log('Patient Fetch (update) for pt data', this.props.micro_id);
      this.fetchPtData();
    }
  }

  resetConstraints() {
    this.props.constraintLoadingReset();
    this.props.constrainByOverlap(false);
    this.props.constrainTree([]);
    this.props.constrainOverlap([]);
  }

  async fetchPtData(overlapLevel = this.props.overlap_level) {
    const response = await this.props.api.get(
      `/pt/${this.props.micro_id}/level/${overlapLevel}/incubation/${this.props.incubation}/inf_period_before/${this.props.inf_period_before}/inf_period_after/${this.props.inf_period_after}/contam/${this.props.contam_period}/test_time_buffer/${this.props.test_time_buffer}`
    );
    if (response.ok) {
      const result = response.body;
      this.props.storePtObj(this.props.micro_id, result);
      // store micro_id and name for viewed patients menu
      this.props.storeViewedObj(this.props.micro_id, result.pt.name);
    } else {
      console.log("error");
    }
  }

  async fetchContactList() {
    //console.log('micro_id', this.props.micro_id);
    var response = await this.props.api.get(
      "/simple_contact_list/" + this.props.micro_id
    );
    if (response.ok) {
      const result = response.body.contacts;
      //console.log('result', result);
      this.props.storeContacts(result);
    } else {
      console.log("error");
    }
  }

  async fetchTree(forceUnconstrained = false) {
    const activeConstraints = forceUnconstrained
      ? []
      : this.props.constrain_tree;
    const constraintKey =
      activeConstraints.length > 0 ? activeConstraints.join(",") : "0";
    const cacheKey = `${this.props.micro_id}|${this.props.snp_distance}|${constraintKey}`;

    const cached = this.treeCache.get(cacheKey);
    if (cached) {
      const nameMapCopy = { ...(cached.name_map || {}) };
      this.props.storeTree(cached.tree, cached.distance_matrix, nameMapCopy);
      return;
    }

    const requestId = ++this.treeRequestId;
    this.props.constraintLoadingStart();

    try {
      const endpoint =
        "/tree/" +
        this.props.micro_id +
        "/snps/" +
        this.props.snp_distance +
        "/constrain/" +
        (activeConstraints.length > 0
          ? activeConstraints
          : "0");

      const response = await this.props.api.get(endpoint);

      if (!response.ok) {
        console.log("error");
        return;
      }

      const result = response.body;
      if (result.tree) {
        let tokens = result.tree
          .split(/\s*(;|\(|\)|,|:)\s*/)
          .filter((token) => token.length > 1);

        const tokensKey = tokens.join(",");
        let nameMapDetails = this.treeDetailsCache.get(tokensKey);

        if (!nameMapDetails) {
          const queryParams = new URLSearchParams();
          tokens.forEach((token) => queryParams.append("seq_ids", token));

          const response1 = await this.props.api.get(
            `/seq_id_list_to_details?${queryParams.toString()}`
          );

          if (!response1.ok) {
            console.log("error");
            nameMapDetails = {};
          } else {
            const result1 = response1.body.details || {};
            nameMapDetails = {};
            for (const token of tokens) {
              const data = result1[token];
              if (data) {
                nameMapDetails[token] = [
                  `${data.firstname} ${data.surname}`,
                  data.micro_id,
                  data.collectdt,
                ];
              }
            }
            this.treeDetailsCache.set(tokensKey, nameMapDetails);
          }
        }

        const name_map = { ...(nameMapDetails || {}) };
        this.treeCache.set(cacheKey, {
          tree: result.tree,
          distance_matrix: result.distance_matrix,
          name_map,
        });

        if (requestId === this.treeRequestId) {
          this.props.storeTree(result.tree, result.distance_matrix, name_map);
        }
      } else {
        const emptyTree = { tree: null };
        this.treeCache.set(cacheKey, {
          tree: emptyTree,
          distance_matrix: {},
          name_map: {},
        });
        if (requestId === this.treeRequestId) {
          this.props.storeTree(emptyTree, {});
        }
      }
    } catch (error) {
      console.error("error", error);
    } finally {
      this.props.constraintLoadingEnd();
    }
  }

  async fetchTreeSnp2Snapshot() {
    // Only fetch if we don't already have it
    if (this.props.name_map_snp2 && Object.keys(this.props.name_map_snp2).length > 0) {
      return;
    }

    const resp = await this.props.api.get(
      `/tree/${this.props.micro_id}/snps/2/constrain/0`
    );

    if (!resp.ok || !resp.body?.tree) return;

    let tokens = resp.body.tree
      .split(/\s*(;|\(|\)|,|:)\s*/).filter(t => t.length > 1);

    const queryParams = new URLSearchParams();
    tokens.forEach(t => queryParams.append("seq_ids", t));

    const respDetails = await this.props.api.get(`/seq_id_list_to_details?${queryParams.toString()}`);
    if (!respDetails.ok) return;

    const details = respDetails.body.details || {};
    const name_map_5 = {};
    for (const token of tokens) {
      const d = details[token];
      if (d) {
        name_map_5[token] = [
          `${d.firstname} ${d.surname}`,
          d.micro_id,
          d.collectdt,
        ];
      }
    }

    this.props.storeTreeSnp2NameMap(name_map_5);
  }

  render() {
    return (
      <div>
        <Body>
          <PatientDashboard micro_id={this.props.micro_id} />
        </Body>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    snp_distance: state.patient.snp_distance,
    viewed_obj: state.patient.viewHistory,
    constrain_tree: state.patient.tree.constrain,
    overlap_level: state.patient.patient.overlap_level,
    contam_period: state.patient.patient.contam_period,
    inf_period_before: state.patient.patient.inf_period_prior,
    inf_period_after: state.patient.patient.inf_period_after,
    incubation: state.patient.patient.incubation,
    test_time_buffer: state.patient.patient.test_time_buffer,
    name_map_snp2: state.patient.tree.name_map_snp2,
  };
}

const actionCreators = {
  storePtObj: patientActions.storePtObject,
  storeViewedObj: patientActions.storeViewedObj,
  storeContacts: patientActions.storeContactList,
  storeTree: patientActions.storeTree,
  storeTreeSnp2NameMap: patientActions.storeTreeSnp2NameMap,
  constraintLoadingStart: patientActions.constraintLoadingStart,
  constraintLoadingEnd: patientActions.constraintLoadingEnd,
  constraintLoadingReset: patientActions.constraintLoadingReset,
  constrainTree: patientActions.constrainTree,
  constrainOverlap: patientActions.constrainOverlap,
  constrainByOverlap: patientActions.constrainByOverlap,
};

export default connect(mapStateToProps, actionCreators)(PatientFetch);
